/**
 * Access agents API — the personal + team introspection surfaces plus the
 * generic session/location share + access-request writes.
 *
 * Contract authority (frozen wire): api-service-go/access_board.go
 * (AccessOverviewResponse, AccessResourceItem/AccessResourcesResponse,
 * AccessStep/AccessExplanationResponse, AccessBoardResponse/Member/Grant) and
 * api-service-go/access_requests.go (AccessRequestResponse). Share bodies:
 * ShareResourceRequest in specs/api/api-service.overlay.yaml.
 *
 * The request lifecycle (approve/deny, owner inbox) lives in accessRequests.ts
 * and is reused here — this module adds the reads + the /shares + create-request
 * writes the two boards need. Every mutating write carries a fresh
 * Idempotency-Key (transport-retry dedup; the load-bearing dedup is server-side).
 */

import { api } from '../client';
import type { Page } from '../types';
import { createLogger } from '@/utils/logger';
import { randomUUID } from '@/lib/uuid';
import type { AccessRequest, AccessRequestListItem } from './accessRequests';

const logger = createLogger('AccessAPI');

/** A resource or intermediary in a derivation. Mirrors Go AccessRef.
 *  `kind` is set only for a `node` ref the server resolved via
 *  LocationRefsByIDs (today: managed_locations) — absent elsewhere. */
export interface AccessRef {
  type: string;
  id: string;
  label?: string;
  kind?: string;
}

/** An owner of the caller's unit — who to ask for access. Mirrors Go AccessLead. */
export interface AccessLead {
  principal_type: 'user' | 'group';
  principal_id: string;
  name?: string;
}

/** One of the caller's role memberships (a group). Mirrors Go AccessRoleRef. */
export interface AccessRoleRef {
  group_id: string;
  name?: string;
}

/** Wire enum for how a grant is reached. Mirrors the Go `source`/`kind` set. */
export type AccessSourceWire =
  | 'direct'
  | 'group_membership'
  | 'public'
  | 'org_cascade'
  | 'patient_care_team'
  | 'team_supervision'
  | 'admin'
  | 'inherited';

/** GET /me/access/overview. Mirrors Go AccessOverviewResponse. */
export interface AccessOverviewResponse {
  placement: AccessRef[]; // my unit -> root (nearest first)
  leads: AccessLead[];
  roles: AccessRoleRef[];
  /** Units the caller can GOVERN (`can_manage`) — the team boards' unit-scope
   *  catalogue (a lead's own unit(s); every unit for a platform admin). */
  managed_locations: AccessRef[];
  counts: Record<string, number>;
  grant_authority: Record<string, number>;
  outgoing_requests: AccessRequest[];
}

/** One reachable resource. Mirrors Go AccessResourceItem. */
export interface AccessResourceItemWire {
  resource: AccessRef;
  effective_role: string;
  source: AccessSourceWire;
  expires_at?: string;
}

/**
 * GET /me/access/resources page. Mirrors Go AccessResourcesResponse: the one
 * list envelope; the next page is the response's `Link: rel="next"`.
 */
export type AccessResourcesResponse = Page<AccessResourceItemWire>;

/** One derivation step. Mirrors Go AccessStep. */
export interface AccessStepWire {
  kind: AccessSourceWire;
  via?: AccessRef;
  relation: string;
  expires_at?: string;
}

/** GET /me/access/resources/{resource_ref}. Mirrors Go AccessExplanationResponse. */
export interface AccessExplanationResponse {
  resource: AccessRef;
  steps: AccessStepWire[];
}

/**
 * One member's grant on the unit node. Composed client-side from the unit's
 * `/shares` page rather than read from a server-side join: the per-unit access
 * picture is an agent view-model, and the platform exposes the generic facts
 * (teams, shares, access-requests, user roles) it is assembled from.
 */
export interface UnitGrant {
  relation: string;
  expires_at?: string;
}

/** One staff member's access picture on a unit. */
export interface UnitAccessMember {
  user_id: string;
  name?: string;
  roles: AccessRoleRef[];
  node_grants: UnitGrant[];
  open_requests: AccessRequest[];
}

/**
 * The composed unit access picture. It carries the unit's ID, not an AccessRef:
 * the unit's own label is already resolved by useUnitScope from the caller's
 * managed units, and re-deriving it here (from, say, a staffing team's name)
 * would put a second, wrong answer for the same fact on screen.
 */
export interface UnitAccessResponse {
  location_id: string;
  members: UnitAccessMember[];
}

/** GET /users/{user_id}/roles — the forward index over role membership. */
export interface UserRoleListResponse extends Page<AccessRoleRef> {
  user_id: string;
}

/** The roles a user holds, narrowed by the server to what the caller may read. */
export async function listUserRoles(userId: string): Promise<AccessRoleRef[]> {
  return api.getAllItems<UserRoleListResponse>(`/users/${encodeURIComponent(userId)}/roles`);
}


/** POST /{sessions|locations}/{id}/shares body. Mirrors Go ShareResourceRequest.
 *  For a user principal supply `id` OR `email` (the server resolves either);
 *  a group principal is addressed by `id`. */
export interface ShareResourceBody {
  principal: { type: 'user' | 'group'; id?: string; email?: string };
  access_level: 'owner' | 'editor' | 'viewer';
  /** ISO-8601; a self-expiring share (422 if the tier has no conditioned form). */
  expires_at?: string;
}

/**
 * A caller-supplied key when the caller has one, a fresh one otherwise.
 *
 * A multi-step flow needs the SAME key across a retry so the step replays
 * instead of repeating: the handover shares the session, seeds the Rapport and
 * signs, and a retry after a mid-flow failure must not share twice. A helper
 * that always minted its own made that impossible to express.
 */
function idemHeaders(key?: string): { headers: Record<string, string> } {
  return { headers: { 'Idempotency-Key': key ?? randomUUID() } };
}

// ---------------------------------------------------------------------------
// Personal reads (/me/access/*)
// ---------------------------------------------------------------------------

export async function getAccessOverview(): Promise<AccessOverviewResponse> {
  return api.get<AccessOverviewResponse>('/me/access/overview');
}

export async function listAccessResources(type: string): Promise<AccessResourcesResponse> {
  const params = new URLSearchParams({ type });
  return api.getPage<AccessResourcesResponse>(`/me/access/resources?${params.toString()}`);
}

/**
 * The ITEM read of the resources collection: why the caller can reach this one
 * resource. `resource` is "<type>:<id>" and goes in the path — it used to be a
 * query parameter under a separate `/me/access/explanation` noun, which gave
 * one resource two names.
 */
export async function getAccessExplanation(resource: string): Promise<AccessExplanationResponse> {
  return api.get<AccessExplanationResponse>(`/me/access/resources/${encodeURIComponent(resource)}`);
}

/**
 * Every page of the caller's reachable resources of one type, following the
 * list's `Link: rel="next"` to its last page.
 */
export async function listAllAccessResources(type: string): Promise<AccessResourceItemWire[]> {
  const params = new URLSearchParams({ type });
  return api.getAllItems<AccessResourcesResponse>(`/me/access/resources?${params.toString()}`);
}

/** GET /access-requests (the caller's OWN requests, any status). */
export async function listMyAccessRequests(): Promise<AccessRequestListItem[]> {
  return api.getAllItems<Page<AccessRequestListItem>>('/access-requests');
}

// ---------------------------------------------------------------------------
// Generic share writes (session + location)
// ---------------------------------------------------------------------------


/** Grant a principal viewer/editor/owner on an org unit (staffing grant). */
export async function shareLocationResource(
  locationId: string,
  body: ShareResourceBody,
): Promise<void> {
  logger.info('Sharing location (staffing grant)', {
    locationId,
    principalType: body.principal.type,
    accessLevel: body.access_level,
    expiring: !!body.expires_at,
  });
  await api.post<unknown>(`/locations/${locationId}/shares`, body, idemHeaders());
}


/** Revoke a principal's unit access. */
export async function revokeLocationShare(
  locationId: string,
  principalType: 'user' | 'group',
  principalId: string,
): Promise<void> {
  logger.info('Revoking location share', { locationId, principalType, principalId });
  await api.delete<undefined>(
    `/locations/${locationId}/shares/${principalType}/${encodeURIComponent(principalId)}`,
    idemHeaders(),
  );
}

// ---------------------------------------------------------------------------
// Create access requests (session sub-resource + patient demographics)
// ---------------------------------------------------------------------------

/**
 * A session invitee denied a sub-resource requests viewer|editor.
 *
 * The target rides the body: access requests are one collection, and the
 * session-shaped path that used to carry it existed only to set these two
 * fields for the same handler.
 */
export async function createSessionAccessRequest(
  sessionId: string,
  body: { access_level: 'viewer' | 'editor'; justification?: string },
): Promise<AccessRequest> {
  logger.info('Creating session access request', { sessionId, accessLevel: body.access_level });
  return api.post<AccessRequest>(
    '/access-requests',
    { resource: { type: 'session', id: sessionId }, ...body },
    idemHeaders(),
  );
}

/**
 * Request the demographics tier on a patient a session concerns (leak-safe).
 *
 * NOT the generic collection above, deliberately. This is a different
 * operation: the only level it accepts is `demographics` (viewer/editor are
 * rejected, and PHI is never requestable), it requires a session the caller
 * already holds viewer on, it is gated on a deployment feature flag, and every
 * precondition failure answers a 404 byte-identical to the stranger case so
 * patient ids cannot be probed.
 */
export async function createPatientAccessRequest(
  patientId: string,
  body: { session_id: string; justification?: string },
): Promise<AccessRequest> {
  logger.info('Creating patient demographics access request', { patientId });
  return api.post<AccessRequest>(
    `/patients/${patientId}/access-requests`,
    { access_level: 'demographics', ...body },
    idemHeaders(),
  );
}
